import type { SortInboxResponse } from '@signalvote/shared';
import { campaignHeaders, defineInfiniteQuery, defineQuery } from '@/lib/data';
import { apiJson } from './apiFetch';
import { FINANCE_STALE_TIME } from './queryUtils';

/** A demo view: `?live=1` on the ledger's URL refetches every 5 seconds, so a line recorded elsewhere (by the assistant) shows up. */
const liveInterval = (): number | false =>
  typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('live') === '1' ? 5000 : false;

export interface SortInboxFilter {
  kinds: string[];
  q: string;
}

/** Sorted so toggling Expenditure then Contribution, or the reverse, is the same entry and the same URL. */
const sortedKinds = (filter: SortInboxFilter) => [...filter.kinds].sort();

/**
 * ADR-026: the close ledger's display stream, keyset-cursor pages of `GET /api/finance/sort-inbox`
 * (100 a page; the cursor is opaque and round-tripped verbatim). Only `TriageLedger` reads it;
 * every work surface reads the bounded {@link useSortInboxQueue}. A filter change keeps the
 * previous filter's rows as `placeholder`, so a keystroke never unmounts the ledger and its
 * search input. No focus refetch: the payload is large and the write set already refreshes it.
 */
export const useSortInbox = defineInfiniteQuery({
  key: (filter: SortInboxFilter) => ['finance', 'sort-inbox', 'display', sortedKinds(filter), filter.q],
  reads: 'GET /api/finance/sort-inbox',
  fetch: (filter, cursor: string | null, { signal, campaignId }) => {
    const cursorParam = cursor ? `&cursor=${encodeURIComponent(cursor)}` : '';
    const kindParams = sortedKinds(filter)
      .map((kind) => `&kind=${encodeURIComponent(kind)}`)
      .join('');
    const qParam = filter.q ? `&q=${encodeURIComponent(filter.q)}` : '';
    return apiJson<SortInboxResponse>(`/api/finance/sort-inbox?limit=100${cursorParam}${kindParams}${qParam}`, {
      signal,
      headers: campaignHeaders(campaignId),
      error: 'Failed to fetch sort inbox',
    });
  },
  initialCursor: null,
  nextCursor: (last) => last.nextCursor ?? undefined,
  items: (page) => page.items,
  keepPrevious: true,
  staleTime: FINANCE_STALE_TIME,
  refetchInterval: liveInterval,
  refetchOnWindowFocus: false,
});

/**
 * ADR-026: the bounded NeedsAction work feed, `GET /api/finance/sort-inbox?status=needsAction`,
 * unpaginated, with the same envelope scalars as the display stream. Feeds the triage modal,
 * the inbox strip's counts, the close nav badge and the filed-period fee advisories.
 */
export const useSortInboxQueue = defineQuery({
  key: () => ['finance', 'sort-inbox', 'needsAction'],
  reads: 'GET /api/finance/sort-inbox',
  fetch: (_params: undefined, { signal, campaignId }) =>
    apiJson<SortInboxResponse>('/api/finance/sort-inbox?status=needsAction', {
      signal,
      headers: campaignHeaders(campaignId),
      error: 'Failed to fetch sort inbox queue',
    }),
  staleTime: FINANCE_STALE_TIME,
  refetchInterval: liveInterval,
  refetchOnWindowFocus: false,
});
